import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import type { PricingResult } from "@/lib/bom";

function formatAud(value: number): string {
  return value.toLocaleString("en-AU", { style: "currency", currency: "AUD" });
}

export interface TierBreakChartProps {
  result: PricingResult | null;
}

export function TierBreakChart({ result }: TierBreakChartProps) {
  if (!result || result.columns.length === 0) {
    return <p className="text-sm text-muted-foreground">No pricing yet</p>;
  }

  // Ensure data is sorted by quantity
  const data = [...result.columns]
    .sort((a, b) => a.quantity - b.quantity)
    .map((col) => ({
      quantity: col.quantity,
      unitPrice: col.unitPriceAud,
      totalPrice: col.totalPriceAud,
    }));

  return (
    <div className="w-full h-64 mt-4" aria-label="Pricing Curve Chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis 
            dataKey="quantity" 
            tick={{ fontSize: 12, fill: "#64748b" }}
            tickMargin={10}
            axisLine={{ stroke: "#cbd5e1" }}
            tickLine={false}
            label={{ value: "Quantity (units)", position: "insideBottom", offset: -15, fontSize: 13, fill: "#475569" }}
          />
          <YAxis 
            tickFormatter={(value) => formatAud(value)}
            tick={{ fontSize: 12, fill: "#64748b" }}
            tickMargin={10}
            axisLine={{ stroke: "#cbd5e1" }}
            tickLine={false}
            label={{ value: "Unit Price (AUD)", angle: -90, position: "insideLeft", offset: -5, fontSize: 13, fill: "#475569" }}
          />
          <Tooltip 
            formatter={(value) => [formatAud(Number(value)), "Unit Price"]}
            labelFormatter={(label) => `Quantity: ${label}`}
            contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
          />
          <Line 
            type="monotone" 
            dataKey="unitPrice" 
            stroke="#0ea5e9" 
            strokeWidth={3}
            dot={{ r: 4, fill: "#0ea5e9", strokeWidth: 0 }}
            activeDot={{ r: 6, fill: "#0284c7", strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
